import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { Accordion, Col, Form, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { equipmentsGetPersonCars, procountorSalaryMappingNamesQuery } from '../../../../../api/index.js'
import DefaultDatePicker from '../../../../../common/datepicker/DefaultDatePicker.jsx'
import Switch from '../../../../../common/Switch.jsx'
import { generateOptions, localized, maxTwoDecimals } from '../../../../../common/utils.jsx'
import { activeIntegrationsSelector, authSelector } from '../../../../../reducers/index.js'
import { SalaryHistory } from './salaryhistory.jsx'

export function SalaryInformation({
	person,
	setPerson,
	handleSalaryDetails,
	fieldParams,
	formErrors,
	isPersonal,
	salaryHistory = [],
}) {
	const { t } = useTranslation()
	const { supplements } = useSelector(authSelector)
	const prefix = 'salaryInformation'

	const activeIntegrations = useSelector(activeIntegrationsSelector)
	const isProcountorActive = !!activeIntegrations.tokenProcountor
	const { data } = useQuery(procountorSalaryMappingNamesQuery(isProcountorActive))

	function handleCarBenefit({ target: { id } }) {
		const toggle = id === 'carUsageBenefit'
		setPerson({
			...person,
			salaryDetails: {
				...person.salaryDetails,
				carUsageBenefit: toggle,
				freeCarBenefit: !toggle,
			},
		})
	}

	const [showMoreWorkTimeOptions, setShowMoreWorkTimeOptions] = useState(false)
	const [personCarOptions, setPersonCarOptions] = useState([])

	const supplement = (idx) => {
		const propertyName = `supplement${idx}`
		const salaryDetails = person.salaryDetails
		const labelName = isProcountorActive && data ? data.find((item) => item.index === idx)?.supplementName : null
		const label = labelName || supplements[idx - 1]

		return (
			<Form.Group as={Col} xl={3} className="position-relative" controlId={propertyName}>
				<Form.Label>{Number.isNaN(Number(label)) ? label : `${t('Supplement', 'Lisä')} ${idx}`}</Form.Label>
				<Form.Control
					type="number"
					min="0"
					step="0.01"
					pattern="^\d+(?:\.\d{1,2})?$"
					defaultValue={salaryDetails ? salaryDetails[propertyName] : ''}
					onChange={handleSalaryDetails}
					disabled={isPersonal}
				/>
			</Form.Group>
		)
	}

	const fetchPersonCarOptions = useCallback(async () => {
		const carEquipments = (await equipmentsGetPersonCars(person.personId)).data
		const options = []
		for (const car of carEquipments) {
			options.push(<option key={car.id} value={car.id}>{`${car.code} (${car.yearModel})`}</option>)
		}
		setPersonCarOptions(options)
	}, [person.personId])

	useEffect(() => {
		if (Number(person.salaryDetails?.personalBenefit2) === 1) {
			fetchPersonCarOptions()
		}
	}, [person.salaryDetails?.personalBenefit2, fetchPersonCarOptions])

	return (
		<>
			<Row>
				<Form.Group as={Col} controlId="salaryValidFrom">
					<DefaultDatePicker
						id={`${prefix}-salaryValidFrom`}
						name="salaryValidFrom"
						label={t('ValidFrom', 'Voimassaolon alkupvm.')}
						value={person.salaryDetails?.salaryValidFrom ?? ''}
						onChange={(iso) => handleSalaryDetails({ target: { id: 'salaryValidFrom', value: iso ?? '' } })}
						disabled={isPersonal}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.salaryValidFrom}</Form.Control.Feedback>
				</Form.Group>
				<Form.Group as={Col} controlId="salaryValidUntil">
					<DefaultDatePicker
						id={`${prefix}-salaryValidUntil`}
						name="salaryValidUntil"
						label={t('ValidUntil', 'Voimassaolon päättymispvm.')}
						value={person.salaryDetails?.salaryValidUntil ?? ''}
						onChange={(iso) => handleSalaryDetails({ target: { id: 'salaryValidUntil', value: iso ?? '' } })}
						disabled={isPersonal}
					/>
					<Form.Control.Feedback type="invalid">{formErrors.salaryValidUntil}</Form.Control.Feedback>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={4} className="position-relative" controlId="basis">
					<Form.Label>{t('Basis', 'Palkan perusteet')}</Form.Label>
					<Form.Select
						//defaultValue={person.salaryDetails?.basis}
						value={person.salaryDetails?.basis ?? '0'}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					>
						<option value="0">{t('Choose', 'Valitse')}</option>
						<option value="TIME">{t('Time', 'Aika')}</option>
						<option value="ACHIEVEMENT">{t('Achievement', 'Suoritus')}</option>
						<option value="OTHER">{t('OtherSpecify', 'Muu, mikä')}</option>
					</Form.Select>
				</Form.Group>

				{person.salaryDetails?.basis === 'OTHER' && (
					<Form.Group as={Col} xl={4} className="position-relative" controlId="otherBasis">
						<Form.Label>{t('OtherBasis', 'Muu peruste')}</Form.Label>
						<Form.Control
							defaultValue={person.salaryDetails?.otherBasis}
							onChange={handleSalaryDetails}
							isInvalid={!!formErrors.otherBasis}
							maxLength={fieldParams.otherBasis?.maxLength}
							disabled={isPersonal}
						/>
						<Form.Control.Feedback type="invalid">{formErrors.otherBasis}</Form.Control.Feedback>
					</Form.Group>
				)}

				<Form.Group as={Col} xl={4} className="position-relative" controlId="employmentType">
					<Form.Label>{t('EmploymentType', 'Palvelussuhteen tyyppi')}</Form.Label>
					<Form.Select
						//defaultValue={person.salaryDetails?.employmentType}
						value={String(person.salaryDetails?.employmentType ?? '0')}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					>
						{generateOptions(localized.employmentTypes)}
					</Form.Select>
				</Form.Group>
				{Number(person.salaryDetails?.employmentType) === 2 && (
					<Form.Group as={Col} xl={4} className="position-relative" controlId="partTimePercentage">
						<Form.Label>{t('PartTimePercentage', 'Osa-aikaprosentti')}</Form.Label>
						<Form.Control
							type="number"
							min="0"
							step="0.01"
							pattern="^\d+(?:\.\d{1,2})?$"
							defaultValue={person.salaryDetails?.partTimePercentage ?? ''}
							onChange={handleSalaryDetails}
							isInvalid={!!formErrors.partTimePercentage}
							maxLength={fieldParams.partTimePercentage?.maxLength}
							disabled={isPersonal}
						/>
						<Form.Control.Feedback type="invalid">{formErrors.partTimePercentage}</Form.Control.Feedback>
					</Form.Group>
				)}
			</Row>

			<Row>
				<Form.Group as={Col} xl={4} className="position-relative" controlId="formOfSalary">
					<Form.Label>{t('FormOfSalary', 'Palkkausmuoto')}</Form.Label>
					<Form.Select
						//defaultValue={person.salaryDetails?.formOfSalary}
						value={String(person.salaryDetails?.formOfSalary ?? '0')}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					>
						{generateOptions(localized.salaryForms)}
					</Form.Select>
				</Form.Group>

				<Form.Group as={Col} xl={4} className="position-relative" controlId="monthlyWage">
					{Number(person.salaryDetails?.formOfSalary) === 1 && (
						<>
							<Form.Label>{t('MonthlyWage', 'Kuukausipalkka')}</Form.Label>
							<Form.Control
								type="number"
								min="0"
								step="0.01"
								pattern="^\d+(?:\.\d{1,2})?$"
								defaultValue={person.salaryDetails?.monthlyWage ?? ''}
								onChange={handleSalaryDetails}
								disabled={isPersonal}
							/>
						</>
					)}
				</Form.Group>

				<Form.Group as={Col} xl={4} className="position-relative" controlId="currency">
					<Form.Label>{t('Currency', 'Valuutta')}</Form.Label>
					<Form.Select
						key={`currency-${person.personId ?? 'new'}`}
						defaultValue={person.salaryDetails?.currency ?? 'EUR'}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					>
						<option value="EUR">EUR</option>
						<option value="SEK">SEK</option>
						<option value="USD">USD</option>
					</Form.Select>
				</Form.Group>
			</Row>

			<Row>
				{Number(person.salaryDetails?.employmentType) === 2 && Number(person.salaryDetails?.formOfSalary) === 1 && (
					<Form.Group as={Col} xl={4} className="position-relative" controlId="paymentPercentage">
						<Form.Label>{t('PaymentCoefficient', 'Maksukerroin')} (%)</Form.Label>
						<Form.Control
							type="number"
							min="0"
							step="0.01"
							pattern="^\d+(?:\.\d{1,2})?$"
							defaultValue={person.salaryDetails?.paymentPercentage ?? ''}
							onChange={handleSalaryDetails}
							disabled={isPersonal}
						/>
					</Form.Group>
				)}
				{Number(person.salaryDetails?.employmentType) === 2 && Number(person.salaryDetails?.formOfSalary) === 1 && (
					<Form.Group as={Col} xl={4} className="position-relative" controlId="paymentPercentageSalary">
						<Form.Label>{t('PaymentPercentageSalary', 'Maksukertoimen mukainen palkka')}</Form.Label>
						<Form.Control
							type="number"
							min="0"
							step="0.01"
							pattern="^\d+(?:\.\d{1,2})?$"
							value={
								(person.salaryDetails?.paymentPercentageSalary ??
								(person.salaryDetails.paymentPercentage && person.salaryDetails.monthlyWage))
									? maxTwoDecimals((person.salaryDetails.paymentPercentage / 100) * person.salaryDetails.monthlyWage)
									: ''
							}
							onChange={handleSalaryDetails}
							disabled={isPersonal}
						/>
					</Form.Group>
				)}
			</Row>

			<Row>
				<Form.Group as={Col} xl={4} className="position-relative" controlId="hourlyWageATP">
					<Form.Label>{t('HourlyWageATP', 'Aikatuntipalkka')}</Form.Label>
					<Form.Control
						type="number"
						min="0"
						step="0.01"
						pattern="^\d+(?:\.\d{1,2})?$"
						defaultValue={person.salaryDetails?.hourlyWageATP ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={4} className="position-relative" controlId="hourlyWagePTP">
					<Form.Label>{t('HourlyWagePTP', 'Perustuntipalkka')}</Form.Label>
					<Form.Control
						type="number"
						min="0"
						step="0.01"
						pattern="^\d+(?:\.\d{1,2})?$"
						defaultValue={person.salaryDetails?.hourlyWagePTP ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>

				<Form.Group as={Col} xl={4} className="position-relative" controlId="hourlyWageOTP">
					<Form.Label>{t('HourlyWageOTP', 'Ohjetuntipalkka')}</Form.Label>
					<Form.Control
						type="number"
						min="0"
						step="0.01"
						pattern="^\d+(?:\.\d{1,2})?$"
						defaultValue={person.salaryDetails?.hourlyWageOTP ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={3} className="position-relative" controlId="phoneBenefit">
					<Form.Label></Form.Label>
					<Form.Check
						label={t('PhoneBenefit', 'Puhelinetu')}
						defaultChecked={person.salaryDetails?.phoneBenefit}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
					<Form.Label></Form.Label>
				</Form.Group>

				<Form.Group as={Col} xl={3} className="position-relative">
					<Form.Label></Form.Label>
					<Form.Check
						id="bicycleBenefit"
						label={t('BicycleBenefit', 'Polkupyöräetu')}
						defaultChecked={person.salaryDetails?.bicycleBenefit}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
					{person.salaryDetails?.bicycleBenefit && (
						<Switch
							id="bicycleBenefitSubstracts"
							label={t('SubtractsPayWage', 'Vähentää rahapalkkaa')}
							defaultChecked={person.salaryDetails?.bicycleBenefitSubstracts}
							onChange={handleSalaryDetails}
							disabled={isPersonal}
						/>
					)}
				</Form.Group>

				<Form.Group as={Col} xl={3} className="position-relative">
					<Form.Label></Form.Label>
					<Form.Check
						id="mealBenefit"
						label={t('MealBenefit', 'Ravintoetu')}
						defaultChecked={person.salaryDetails?.mealBenefit}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
					{person.salaryDetails?.mealBenefit && (
						<Switch
							id="mealBenefitTaxableValue"
							label={t('MatchTaxValue', 'Vastaa verotusarvoa')}
							defaultChecked={person.salaryDetails?.mealBenefitTaxableValue}
							onChange={handleSalaryDetails}
							disabled={isPersonal}
						/>
					)}
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={4} className="position-relative" controlId="personalBenefit1">
					<Form.Label>{t('PersonalBenefit1', 'Henk.koht luontoisetu')}</Form.Label>
					<Form.Select
						//defaultValue={person.salaryDetails?.personalBenefit1 ?? "0"}
						value={String(person.salaryDetails?.personalBenefit1 ?? '0')}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					>
						{generateOptions(localized.personalBenefit).filter((_v, idx) => idx !== 1)}
					</Form.Select>
				</Form.Group>

				<Form.Group as={Col} xl={4} className="position-relative" controlId="personalBenefit2">
					<Form.Label>{t('PersonalBenefit2', 'Henk.koht luontoisetu 2')}</Form.Label>
					<Form.Select
						//defaultValue={person.salaryDetails?.personalBenefit2 ?? "0"}
						value={String(person.salaryDetails?.personalBenefit2 ?? '0')}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					>
						{generateOptions(localized.personalBenefit)}
					</Form.Select>
				</Form.Group>
			</Row>
			<Row>
				<Form.Group as={Col} xl={4} className="position-relative" controlId="personalBenefitPlaceholder">
					<Form.Label></Form.Label>
				</Form.Group>

				{Number(person.salaryDetails?.personalBenefit2) === 1 && (
					<>
						<Form.Group as={Col} xl={2} className="position-relative" controlId="carUsageBenefit">
							<Form.Label></Form.Label>
							<Form.Check
								type="radio"
								name="carBenefit"
								label={t('LimitedCarBenefit', 'Käyttöetu')}
								defaultChecked={person.salaryDetails?.carUsageBenefit}
								onChange={handleCarBenefit}
								disabled={isPersonal}
							/>
							<Form.Label></Form.Label>
						</Form.Group>
						<Form.Group as={Col} xl={2} className="position-relative" controlId="freeCarBenefit">
							<Form.Label></Form.Label>
							<Form.Check
								type="radio"
								name="carBenefit"
								label={t('FullCarBenefit', 'Vapaa autoetu')}
								defaultChecked={person.salaryDetails?.freeCarBenefit}
								onChange={handleCarBenefit}
								disabled={isPersonal}
							/>
							<Form.Label></Form.Label>
						</Form.Group>
						{personCarOptions.length > 0 && (
							<Form.Group as={Col} xl={4} controlId="carBenefitEquipmentId">
								<Form.Label>{t('SelectCar', 'Valitse auto')}</Form.Label>
								<Form.Select
									onChange={handleSalaryDetails}
									//defaultValue={person.salaryDetails?.carBenefitEquipmentId ?? "0"}
									value={String(person.salaryDetails?.carBenefitEquipmentId ?? '0')}
									disabled={isPersonal}
								>
									<option value="0">{t('Choose', 'Valitse')}</option>
									{personCarOptions}
								</Form.Select>
								<Form.Label></Form.Label>
							</Form.Group>
						)}
					</>
				)}
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} className="position-relative" controlId="otherBenefits">
					<Form.Label>{t('OtherBenefitsAndSalaryMatters', 'Muut luontoisedut ja palkkaan liittyvät asiat')}</Form.Label>
					<Form.Control
						as="textarea"
						defaultValue={person.salaryDetails?.otherBenefits ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={6} className="position-relative" controlId="period">
					<Form.Label>{t('PaymentPeriod', 'Maksukausi')}</Form.Label>
					<Form.Control
						defaultValue={person.salaryDetails?.period ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
				<Form.Group as={Col} xl={6} className="position-relative" controlId="day">
					<Form.Label>{t('Day', 'Maksupäivä')}</Form.Label>
					<Form.Control
						defaultValue={person.salaryDetails?.day ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={3} className="position-relative" controlId="weeklyWorkHours">
					<Form.Label>{t('WeeklyWorkHours', 'Viikkotyöaika')}</Form.Label>
					<Form.Control
						type="number"
						defaultValue={person.salaryDetails?.weeklyWorkHours ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
				<Form.Group as={Col} xl={3} className="position-relative mt-3" controlId="showMoreWorkTimeOptions">
					<Form.Check
						type="checkbox"
						label={t('ShowMoreWorkTimeOptions', 'Näytä lisää työaikavaihtoehtoja')}
						checked={showMoreWorkTimeOptions}
						onChange={() => setShowMoreWorkTimeOptions(!showMoreWorkTimeOptions)}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Row>
				{showMoreWorkTimeOptions && (
					<Form.Group as={Col} xl={3} className="position-relative" controlId="dailyWorkHours">
						<Form.Label>{t('DailyHours', 'Vuorokautinen työaika')}</Form.Label>
						<Form.Control
							type="number"
							defaultValue={person.salaryDetails?.dailyWorkHours ?? ''}
							onChange={handleSalaryDetails}
							disabled={isPersonal}
						/>
					</Form.Group>
				)}
			</Row>

			<Row>
				{showMoreWorkTimeOptions && (
					<Form.Group as={Col} xl={3} className="position-relative" controlId="workHours">
						<Form.Label>{t('Hours', 'Tuntia')}</Form.Label>
						<Form.Control
							type="number"
							defaultValue={person.salaryDetails?.workHours ?? ''}
							onChange={handleSalaryDetails}
							disabled={isPersonal}
						/>
					</Form.Group>
				)}
				{showMoreWorkTimeOptions && (
					<Form.Group as={Col} xl={9} className="position-relative" controlId="workCycle">
						<Form.Label></Form.Label>
						<div className="mb-3">
							<Form.Check
								type="radio"
								name="workCycle"
								id="workCycle1"
								label={t('OneWeekPeriod', 'Viikon jaksoissa')}
								inline
								value="1"
								defaultChecked={person.salaryDetails?.workCycle === 1}
								onChange={handleSalaryDetails}
								disabled={isPersonal}
							/>
							<Form.Check
								type="radio"
								name="workCycle"
								id="workCycle2"
								label={t('TwoWeekPeriod', 'Kahden viikon jaksoissa')}
								inline
								value="2"
								defaultChecked={person.salaryDetails?.workCycle === 2}
								onChange={handleSalaryDetails}
								disabled={isPersonal}
							/>
							<Form.Check
								type="radio"
								name="workCycle"
								id="workCycle3"
								label={t('ThreeWeekPeriod', 'Kolmen viikon jaksoissa')}
								inline
								value="3"
								defaultChecked={person.salaryDetails?.workCycle === 3}
								onChange={handleSalaryDetails}
								disabled={isPersonal}
							/>
							<Form.Check
								type="radio"
								name="workCycle"
								id="workCycle4"
								label={t('YearPeriod', 'Vuodessa')}
								inline
								value="4"
								defaultChecked={person.salaryDetails?.workCycle === 4}
								onChange={handleSalaryDetails}
								disabled={isPersonal}
							/>
						</div>
					</Form.Group>
				)}
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} className="position-relative" controlId="workHoursInformation">
					<Form.Label>{t('WorkHoursInformation', 'Työajan lisätiedot')}</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						defaultValue={person.salaryDetails?.workHoursInformation ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Row>
				<Form.Group as={Col} xl={12} className="position-relative" controlId="vacationInformation">
					<Form.Label>{t('VacationInformation', 'Vuosilomat')}</Form.Label>
					<Form.Control
						as="textarea"
						rows={3}
						defaultValue={person.salaryDetails?.vacationInformation ?? ''}
						onChange={handleSalaryDetails}
						disabled={isPersonal}
					/>
				</Form.Group>
			</Row>

			<Form.Group className="mb-3" controlId="experienceCode">
				<Form.Label>{t('ExperienceCode', 'Kokemuskoodi')}</Form.Label>
				<Form.Control
					onChange={handleSalaryDetails}
					defaultValue={person.salaryDetails?.experienceCode}
					isInvalid={!!formErrors.experienceCode}
					maxLength={fieldParams.experienceCode?.maxLength}
					disabled={isPersonal}
				/>
				<Form.Control.Feedback type="invalid">{formErrors.experienceCode}</Form.Control.Feedback>
			</Form.Group>

			<Accordion flush alwaysOpen>
				<Accordion.Item key="supplements" eventKey="supplements">
					<Accordion.Header>{t('Supplements', 'Palkanlisät')}</Accordion.Header>
					<Accordion.Body className="d-flex flex-column">
						<Row>
							{supplement(1)}
							{supplement(2)}
							{supplement(3)}
							{supplement(4)}
						</Row>

						<Row>
							{supplement(5)}
							{supplement(6)}
							{supplement(7)}
							{supplement(8)}
						</Row>

						<Row>
							{supplement(9)}
							{supplement(10)}
							{supplement(11)}
							{supplement(12)}
						</Row>

						<Row>
							{supplement(13)}
							{supplement(14)}
							{supplement(15)}
							{supplement(16)}
						</Row>

						<Row>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="annualSupplement1">
								<Form.Label>{t('AnnualSupplement', 'Vuosisidonnainen lisä')} 1</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.annualSupplement1 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="fixedAnnualSupplement1">
								<Form.Label>{t('FixedAnnualSupplement', 'Määrävuosilisä')} 1</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.fixedAnnualSupplement1 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="annualSupplement2">
								<Form.Label>{t('AnnualSupplement', 'Vuosisidonnainen lisä')} 2</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.annualSupplement2 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="fixedAnnualSupplement2">
								<Form.Label>{t('FixedAnnualSupplement', 'Määrävuosilisä')} 2</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.fixedAnnualSupplement2 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="annualSupplement3">
								<Form.Label>{t('AnnualSupplement', 'Vuosisidonnainen lisä')} 3</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.annualSupplement3 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="fixedAnnualSupplement3">
								<Form.Label>{t('FixedAnnualSupplement', 'Määrävuosilisä')} 3</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.fixedAnnualSupplement3 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>

						<Row>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="annualSupplement4">
								<Form.Label>{t('AnnualSupplement', 'Vuosisidonnainen lisä')} 4</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.annualSupplement4 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
							<Form.Group as={Col} xl={6} className="position-relative" controlId="fixedAnnualSupplement4">
								<Form.Label>{t('FixedAnnualSupplement', 'Määrävuosilisä')} 4</Form.Label>
								<Form.Control
									type="number"
									min="0"
									step="0.01"
									pattern="^\d+(?:\.\d{1,2})?$"
									defaultValue={person.salaryDetails?.fixedAnnualSupplement4 ?? ''}
									onChange={handleSalaryDetails}
									disabled={isPersonal}
								/>
							</Form.Group>
						</Row>
					</Accordion.Body>
				</Accordion.Item>

				{person.nr && <SalaryHistory salaryHistory={salaryHistory} />}
			</Accordion>
		</>
	)
}
